// batch-65 inkoop-kosten (FO-482) — kostensoorten als beheerbare stamtabel met
// hoofdgroepen (vervangt de vlakke enum `InkoopKostensoort`).
//
// Gedeelde Zod-schema's (API .safeParse + client-side prevalidatie, één bron —
// CLAUDE.md "Validatie & foutmeldingen"), DTO's en pure helpers voor de keuzelijst.
// De startvulling staat in `kostensoorten-seed.ts`; de detail-classificaties van
// brandstof (FO-460), garage (FO-462) en telefoon (FO-463) mappen hieronder op een
// stamtabel-code ("één waarheid voor rapportage: de kostensoort").

import { z } from 'zod'
import { idParam } from './id-validators'
import type { BrandstofKostensoort } from './kostprijs-werkelijk'
import type { TelefoonKostensoort } from './telefonie'
import type { GarageKostenCategorie } from './garage-voertuig'
import type { OnderhoudRegelClassificatie } from './garage-onderhoud'

// ─── Relevantie per doeltype (sortering, niets geblokkeerd) ─────────────────

/**
 * Voor welke kostendrager een soort primair relevant is. MEDEWERKER/VOERTUIG/
 * DRAGER spiegelen de interne doeltypen; KLANT dekt de klant-, werkbon- en
 * projecttoerekening (FO-483); ALGEMEEN = bedrijfsbrede kosten zonder drager.
 * Puur sortering in de keuzelijst: elke soort blijft altijd kiesbaar.
 */
export const KOSTENSOORT_RELEVANTIE = ['MEDEWERKER', 'VOERTUIG', 'DRAGER', 'KLANT', 'ALGEMEEN'] as const
export type KostensoortRelevantie = (typeof KOSTENSOORT_RELEVANTIE)[number]

export const KOSTENSOORT_RELEVANTIE_LABELS: Record<KostensoortRelevantie, string> = {
  MEDEWERKER: 'Medewerker',
  VOERTUIG: 'Voertuig',
  DRAGER: 'Kar/koffer',
  KLANT: 'Klant, werkbon of project',
  ALGEMEEN: 'Algemeen (geen drager)',
}

/**
 * Het doeltype van een toewijzing -> de relevantie-as van de stamtabel. De drie
 * klant-doeltypen (FO-483) vallen samen onder KLANT; zonder toewijzing is de regel
 * een algemene kost.
 */
export function kostensoortRelevantieVoorDoeltype(doeltype: string | null | undefined): KostensoortRelevantie {
  switch (doeltype) {
    case 'MEDEWERKER':
      return 'MEDEWERKER'
    case 'VOERTUIG':
      return 'VOERTUIG'
    case 'DRAGER':
      return 'DRAGER'
    case 'KLANT':
    case 'WERKBON':
    case 'PROJECT':
      return 'KLANT'
    default:
      return 'ALGEMEEN'
  }
}

// ─── Herkomst van de kostensoort op een regel ───────────────────────────────

export const KOSTENSOORT_BRON = [
  'HANDMATIG',
  'BESTELTRAJECT',
  'HERHALING',
  'GEHEUGEN',
  'ZOEKWOORD',
  'LEVERANCIER',
  'DETAIL',
  'AI',
  'MIGRATIE',
] as const
export type KostensoortBron = (typeof KOSTENSOORT_BRON)[number]

export const KOSTENSOORT_BRON_LABELS: Record<KostensoortBron, string> = {
  HANDMATIG: 'Handmatig gekozen',
  BESTELTRAJECT: 'Uit de bestelaanvraag',
  HERHALING: 'Zoals de vorige factuur',
  GEHEUGEN: 'Op basis van eerdere keuze',
  ZOEKWOORD: 'Herkend aan de omschrijving',
  LEVERANCIER: 'Standaard van de leverancier',
  DETAIL: 'Afgeleid uit het doel of de brandstof-, garage- of telefoonindeling',
  AI: 'Voorstel van de AI',
  MIGRATIE: 'Overgenomen uit de oude indeling',
}

/** Bronnen die een mens heeft bevestigd: een automatische bron overschrijft die nooit. */
export function isHandmatigeKostensoortBron(bron: KostensoortBron | null | undefined): boolean {
  return bron === 'HANDMATIG'
}

// ─── Code-conventie ─────────────────────────────────────────────────────────

/** Codes: hoofdletters, cijfers en underscores; stabiel en onveranderlijk na aanmaken. */
export const KOSTENSOORT_CODE_REGEX = /^[A-Z][A-Z0-9_]{1,39}$/

const codeSchema = z
  .string()
  .trim()
  .transform((s) => s.toUpperCase())
  .pipe(
    z
      .string()
      .regex(KOSTENSOORT_CODE_REGEX, 'Code: 2 tot 40 tekens, hoofdletters, cijfers of _ (begint met een letter).'),
  )

const naamSchema = z.string().trim().min(1, 'Vul een naam in.').max(100, 'Maximaal 100 tekens.')

/** Zoekwoorden: kleine letters, ontdubbeld, lege weggelaten, maximaal 60 stuks. */
const zoekwoordenSchema = z
  .array(z.string().trim().max(60, 'Een zoekwoord mag maximaal 60 tekens zijn.'))
  .max(60, 'Maximaal 60 zoekwoorden.')
  .transform((lijst) => [...new Set(lijst.map((w) => w.toLowerCase()).filter((w) => w.length > 0))])

const grootboekSchema = z
  .string()
  .trim()
  .max(20, 'Maximaal 20 tekens.')
  .nullish()
  .transform((v) => (v && v.length > 0 ? v : null))

// ─── Schrijfpad: beheer (Instellingen > Kosten) ─────────────────────────────

export const kostenHoofdgroepCreateSchema = z.object({
  code: codeSchema,
  naam: naamSchema,
})
export type KostenHoofdgroepCreateInput = z.infer<typeof kostenHoofdgroepCreateSchema>

export const kostenHoofdgroepUpdateSchema = z
  .object({
    naam: naamSchema.optional(),
    actief: z.boolean().optional(),
  })
  .refine((d) => d.naam !== undefined || d.actief !== undefined, {
    message: 'Geef minstens één wijziging op.',
  })
export type KostenHoofdgroepUpdateInput = z.infer<typeof kostenHoofdgroepUpdateSchema>

export const kostensoortCreateSchema = z.object({
  code: codeSchema,
  naam: naamSchema,
  hoofdgroepId: idParam('hoofdgroepId'),
  relevantVoorDoeltype: z.array(z.enum(KOSTENSOORT_RELEVANTIE)).max(KOSTENSOORT_RELEVANTIE.length).default([]),
  zoekwoorden: zoekwoordenSchema.default([]),
  grootboekrekening: grootboekSchema,
  btwAftrekbaar: z.boolean().default(true),
})
export type KostensoortCreateInput = z.infer<typeof kostensoortCreateSchema>

export const kostensoortUpdateSchema = z.object({
  naam: naamSchema.optional(),
  hoofdgroepId: idParam('hoofdgroepId').optional(),
  relevantVoorDoeltype: z.array(z.enum(KOSTENSOORT_RELEVANTIE)).max(KOSTENSOORT_RELEVANTIE.length).optional(),
  zoekwoorden: zoekwoordenSchema.optional(),
  grootboekrekening: grootboekSchema.optional(),
  btwAftrekbaar: z.boolean().optional(),
  actief: z.boolean().optional(),
  /**
   * FO-482 §6.1 — deactiveren van een soort die leveranciersdefault is geeft eerst
   * een melding met de leveranciers (409); met `true` bevestigt de beheerder dat.
   */
  bevestigLeveranciersDefault: z.boolean().optional(),
})
export type KostensoortUpdateInput = z.infer<typeof kostensoortUpdateSchema>

/** Nieuwe volgorde: de ids in de gewenste volgorde (hoofdgroepen, of soorten binnen één groep). */
export const kostensoortVolgordeSchema = z.object({
  hoofdgroepId: idParam('hoofdgroepId').optional(),
  ids: z.array(idParam()).min(1).max(500),
})
export type KostensoortVolgordeInput = z.infer<typeof kostensoortVolgordeSchema>

/** Samenvoegen = "verplaats alle regels naar ..." (bron wordt gedeactiveerd, nooit verwijderd). */
export const kostensoortSamenvoegenSchema = z.object({
  doelKostensoortId: idParam('doelKostensoortId'),
  toelichting: z.string().trim().min(3, 'Geef een korte reden voor het samenvoegen.').max(500),
})
export type KostensoortSamenvoegenInput = z.infer<typeof kostensoortSamenvoegenSchema>

/** Inline de kostensoort van één regel zetten (werkvoorraad "zonder kostensoort"). */
export const inkoopRegelKostensoortSchema = z.object({
  kostensoortId: idParam('kostensoortId').nullable(),
})
export type InkoopRegelKostensoortInput = z.infer<typeof inkoopRegelKostensoortSchema>

export const regelsZonderKostensoortQuerySchema = z.object({
  van: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  tot: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  leverancierId: idParam('leverancierId').optional(),
  entityId: idParam('entityId').optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(200).default(50),
})
export type RegelsZonderKostensoortQuery = z.infer<typeof regelsZonderKostensoortQuerySchema>

export const kostensoortAuditQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(200).default(50),
})

// ─── Read-DTO's ─────────────────────────────────────────────────────────────

export interface KostenHoofdgroepDTO {
  id: string
  code: string
  naam: string
  volgorde: number
  actief: boolean
}

export interface KostensoortDTO {
  id: string
  code: string
  naam: string
  hoofdgroepId: string
  hoofdgroepCode: string
  hoofdgroepNaam: string
  volgorde: number
  actief: boolean
  relevantVoorDoeltype: KostensoortRelevantie[]
  zoekwoorden: string[]
  grootboekrekening: string | null
  btwAftrekbaar: boolean
  /** De oude enum-waarde waaruit deze soort is gemigreerd (null = nieuwe soort). */
  legacyEnum: string | null
}

/** Compacte verwijzing op een regel of toewijzing (weergave "Hoofdgroep › Soort"). */
export interface KostensoortRefDTO {
  id: string
  code: string
  naam: string
  hoofdgroepCode: string
  hoofdgroepNaam: string
  actief: boolean
}

/**
 * De keuzelijst voor een regel. `recentVanLeverancier` = kostensoort-ids die bij
 * deze leverancier recent gekozen zijn (nieuwste eerst) — die staan bovenaan.
 * Inactieve soorten zitten erin (historische regels tonen hun soort), maar de
 * keuzelijst biedt ze alleen aan als de regel ze al draagt.
 */
export interface KostensoortCatalogusDTO {
  hoofdgroepen: KostenHoofdgroepDTO[]
  soorten: KostensoortDTO[]
  recentVanLeverancier: string[]
}

export interface KostensoortLeverancierRefDTO {
  id: string
  naam: string
}

/** Beheer-rij: de soort plus hoe vaak hij gebruikt wordt. */
export interface KostensoortBeheerDTO extends KostensoortDTO {
  aantalRegels: number
  leveranciersDefault: KostensoortLeverancierRefDTO[]
}

export interface KostenHoofdgroepBeheerDTO extends KostenHoofdgroepDTO {
  soorten: KostensoortBeheerDTO[]
}

export interface KostensoortBeheerOverzichtDTO {
  hoofdgroepen: KostenHoofdgroepBeheerDTO[]
  /** Server-side vlag: mag de ingelogde gebruiker beheren (INKOOP.canApprove, geen ACCOUNTANT)? */
  magBeheren: boolean
}

export const KOSTENSOORT_AUDIT_ACTIE = [
  'AANGEMAAKT',
  'BIJGEWERKT',
  'GEDEACTIVEERD',
  'GEACTIVEERD',
  'VOLGORDE',
  'SAMENGEVOEGD',
] as const
export type KostensoortAuditActie = (typeof KOSTENSOORT_AUDIT_ACTIE)[number]

export const KOSTENSOORT_AUDIT_ACTIE_LABELS: Record<KostensoortAuditActie, string> = {
  AANGEMAAKT: 'Aangemaakt',
  BIJGEWERKT: 'Bijgewerkt',
  GEDEACTIVEERD: 'Gedeactiveerd',
  GEACTIVEERD: 'Weer geactiveerd',
  VOLGORDE: 'Volgorde gewijzigd',
  SAMENGEVOEGD: 'Samengevoegd',
}

export interface KostensoortAuditDTO {
  id: string
  /** INSTELLING: een kosten-instelling (entiteitId = de veldnaam), bv. de standaardopslag. */
  entiteit: 'HOOFDGROEP' | 'SOORT' | 'INSTELLING'
  entiteitId: string
  /** "Hoofdgroep › Soort" of de hoofdgroepnaam op het moment van de wijziging. */
  omschrijving: string
  actie: KostensoortAuditActie
  oudeWaarde: unknown
  nieuweWaarde: unknown
  toelichting: string | null
  actorNaam: string | null
  createdAt: string
}

export interface KostensoortAuditLijstDTO {
  items: KostensoortAuditDTO[]
  meta: { total: number; page: number; pageSize: number }
}

/** Resultaat van samenvoegen (voor de bevestiging in de UI). */
export interface KostensoortSamenvoegResultaatDTO {
  bronId: string
  doelId: string
  verplaatsteRegels: number
  verplaatsteLeveranciersDefaults: number
  verplaatstGeheugen: number
}

/** Werkvoorraad-rij "regels zonder kostensoort". */
export interface RegelZonderKostensoortDTO {
  regelId: string
  factuurId: string
  factuurNr: string
  leverancierId: string
  leverancierNaam: string
  entityId: string | null
  bvNaam: string | null
  /** Factuurdatum (yyyy-MM-dd, Europe/Amsterdam). */
  datum: string
  omschrijving: string
  totaal: number
  /** Het AI-voorstel dat (nog) niet is overgenomen, als hint. */
  aiVoorstel: KostensoortRefDTO | null
}

export interface RegelsZonderKostensoortDTO {
  regels: RegelZonderKostensoortDTO[]
  /** Som van de regelbedragen over de VOLLEDIGE gefilterde set (server-side). */
  totaalBedrag: number
  meta: { total: number; page: number; pageSize: number }
}

// ─── Keuzelijst (pure helpers) ──────────────────────────────────────────────

/** Weergave "Hoofdgroep › Soort". */
export function kostensoortVolledigeNaam(s: { naam: string; hoofdgroepNaam: string }): string {
  return `${s.hoofdgroepNaam} › ${s.naam}`
}

/** Een optie in de gegroepeerde keuzelijst (structureel gelijk aan `ComboboxOption` uit @streep/ui). */
export interface KostensoortKeuzeOptie {
  value: string
  label: string
  hint?: string
  group?: string
  /** Onzichtbare extra zoektermen (hoofdgroep + zoekwoorden). */
  keywords?: string[]
  disabled?: boolean
}

export const KOSTENSOORT_GROEP_RECENT = 'Recent bij deze leverancier'

/**
 * De gegroepeerde keuzelijst voor een regel (FO-482 §3.3): eerst de recent bij
 * deze leverancier gekozen soorten, dan per hoofdgroep (hoofdgroepvolgorde) met
 * de voor het doeltype relevante soorten bovenaan binnen hun groep. Niets wordt
 * geblokkeerd; inactieve soorten verschijnen alleen als de regel ze al draagt
 * (historie blijft leesbaar). Elke soort staat precies één keer in de lijst.
 */
export function kostensoortKeuzeOpties(
  catalogus: Pick<KostensoortCatalogusDTO, 'hoofdgroepen' | 'soorten'>,
  opts: {
    doeltype?: string | null
    recentIds?: readonly string[]
    huidigeId?: string | null
    /** Maximaal zoveel recente soorten bovenaan (default 5). */
    maxRecent?: number
  } = {},
): KostensoortKeuzeOptie[] {
  const relevantie = kostensoortRelevantieVoorDoeltype(opts.doeltype ?? null)
  const groepVolgorde = new Map(catalogus.hoofdgroepen.map((h) => [h.id, h.volgorde]))
  const groepActief = new Map(catalogus.hoofdgroepen.map((h) => [h.id, h.actief]))
  const zichtbaar = catalogus.soorten.filter(
    (s) => s.id === opts.huidigeId || (s.actief && groepActief.get(s.hoofdgroepId) !== false),
  )
  const perId = new Map(zichtbaar.map((s) => [s.id, s]))

  const recent: KostensoortDTO[] = []
  for (const id of opts.recentIds ?? []) {
    const s = perId.get(id)
    if (s && s.actief && !recent.includes(s)) recent.push(s)
    if (recent.length >= (opts.maxRecent ?? 5)) break
  }
  const recentSet = new Set(recent.map((s) => s.id))

  const rest = zichtbaar
    .filter((s) => !recentSet.has(s.id))
    .sort((a, b) => {
      const ga = groepVolgorde.get(a.hoofdgroepId) ?? 0
      const gb = groepVolgorde.get(b.hoofdgroepId) ?? 0
      if (ga !== gb) return ga - gb
      const ra = a.relevantVoorDoeltype.includes(relevantie) ? 0 : 1
      const rb = b.relevantVoorDoeltype.includes(relevantie) ? 0 : 1
      if (ra !== rb) return ra - rb
      if (a.volgorde !== b.volgorde) return a.volgorde - b.volgorde
      return a.naam.localeCompare(b.naam, 'nl')
    })

  const naarOptie = (s: KostensoortDTO, group: string, metGroepHint: boolean): KostensoortKeuzeOptie => ({
    value: s.id,
    label: s.naam,
    group,
    ...(metGroepHint ? { hint: s.hoofdgroepNaam } : {}),
    keywords: [s.hoofdgroepNaam, s.code, ...s.zoekwoorden],
    ...(s.actief ? {} : { disabled: true, hint: 'niet meer actief' }),
  })

  return [
    ...recent.map((s) => naarOptie(s, KOSTENSOORT_GROEP_RECENT, true)),
    ...rest.map((s) => naarOptie(s, s.hoofdgroepNaam, false)),
  ]
}

// ─── Detail-classificaties -> stamtabel-code (FO-460/462/463) ───────────────
//
// Brandstof, garage en telefoon houden hun eigen detail-enums (een andere as:
// wat-binnen-de-factuur), maar zetten de kostensoort van de inkoopfactuurregel op
// de bijbehorende stamtabelwaarde. Elke code hieronder bestaat in de seed; de
// drift-test bewaakt dat.

export const BRANDSTOF_KOSTENSOORT_STAMCODE: Readonly<Record<BrandstofKostensoort, string>> = {
  BRANDSTOF: 'BRANDSTOF',
  ADBLUE: 'BRANDSTOF',
  WASSEN: 'WAGEN_WASSEN',
  TOL: 'WAGEN_TOL',
  KAARTKOSTEN: 'WAGEN_KAARTKOSTEN',
  // Een brandstoftoeslag hoort bij de brandstof zelf.
  TOESLAG: 'BRANDSTOF',
  OVERIG: 'OVERIG',
}

export const TELEFOON_KOSTENSOORT_STAMCODE: Readonly<Record<TelefoonKostensoort, string>> = {
  ABONNEMENT: 'ICT_TELEFONIE',
  VERBRUIK: 'ICT_TELEFONIE',
  EENMALIG: 'ICT_TELEFONIE',
  TOESTEL: 'ICT_MOBIEL',
  // Een korting verlaagt de post waar hij bij hoort: het abonnement.
  KORTING: 'ICT_TELEFONIE',
  OVERIG: 'ICT_TELEFONIE',
}

export const GARAGE_KOSTEN_CATEGORIE_STAMCODE: Readonly<Record<GarageKostenCategorie, string>> = {
  ONDERHOUD: 'ONDERHOUD_REPARATIE',
  SCHADE_REPARATIE: 'SCHADE_EIGEN_RISICO',
}

/** Onderhoudsclassificatie van een garageregel; OVERIG = geen eenduidige soort. */
export const ONDERHOUD_CLASSIFICATIE_STAMCODE: Readonly<Record<OnderhoudRegelClassificatie, string | null>> = {
  BEURT_KLEIN: 'ONDERHOUD_REPARATIE',
  BEURT_GROOT: 'ONDERHOUD_REPARATIE',
  REPARATIE: 'ONDERHOUD_REPARATIE',
  APK_KEURING: 'KEURING',
  BANDEN: 'BANDEN',
  SCHADE: 'SCHADE_EIGEN_RISICO',
  OVERIG: null,
  // batch-68 werkplaatsfacturen (FO-341) - bijvullen/klein onderhoud = onderhoud.
  KLEIN_ONDERHOUD: 'ONDERHOUD_REPARATIE',
}
